<script setup lang="ts">
    const emits = defineEmits<{
        (
            e: 'toggle',
            /** will return true if open */
            data: boolean,
        ): void;
    }>();

    const appStore = useAppStore();
    const { showAddToCart } = storeToRefs(appStore);
    const { accountEnabled } = useAccount();

    const isOpen = ref<boolean>(false);
    watch(isOpen, (newIsOpen) => {
        appStore.setMobileMenuOpen(newIsOpen);
    });

    const toggle = () => {
        isOpen.value = !isOpen.value;
        emits('toggle', isOpen.value);
    };
    const close = () => {
        emits('toggle', false);
        isOpen.value = false;
    };
    useOnNavigation(close);
</script>
<template>
    <nav
        :class="[
            'mol-header-mobile-nav-bar fixed z-xl flex w-full items-center justify-around border-woom-grey-taupe bg-white px-6 py-1 drop-shadow transition-colors lg:hidden',
            { 'open border-t': isOpen },
        ]">
        <atm-helpscout-button
            :class="{ 'opacity-0': showAddToCart && !isOpen }"
            id="navigation_helpscout" />
        <lazy-atm-account-button
            v-if="accountEnabled"
            :class="{ 'opacity-0': showAddToCart && !isOpen }"
            id="navigation_account" />
        <!-- opacity-0 can not be on <atm-cart-button />, as it is potentially overwritten by the internal disabled state with opacity-60 -->
        <div :class="{ 'opacity-0': showAddToCart && !isOpen }">
            <atm-cart-button id="navigation_cart" />
        </div>
        <mol-algolia-autocomplete />
        <button
            :aria-label="isOpen ? $t('cta.menu.openMobile') : $t('cta.menu.closeMobile')"
            class="relative h-12 w-12 transition-colors hover:text-woom-red focus:text-woom-red"
            id="navigation_menu"
            @click="toggle()">
            <atm-cart-items-bubble v-if="!isOpen && showAddToCart" />
            <svg
                class="m-auto h-6 w-6 fill-current"
                viewBox="0 0 25 24"
                xmlns="http://www.w3.org/2000/svg">
                <path
                    :class="['burger-line burger-line-top', { 'burger-open': isOpen }]"
                    d="M18.0117 5.00503C18.7717 5.00679 19.4104 5.00897 19.8643 5.01186C20.0905 5.01331 20.2717 5.015 20.3994 5.01675C20.4632 5.01762 20.5155 5.01772 20.5527 5.0187C20.5712 5.01919 20.5877 5.02005 20.6006 5.02065L20.6504 5.02554L20.7686 5.05288C21.0391 5.13107 21.2709 5.31209 21.4023 5.55972L21.4443 5.64858C21.4567 5.67974 21.4674 5.71384 21.4746 5.75307C21.4872 5.82146 21.4873 5.89824 21.4873 5.98647C21.4873 6.07512 21.4869 6.15364 21.4736 6.2228C21.4658 6.26337 21.4548 6.29865 21.4414 6.33022L21.3965 6.41714C21.3183 6.55677 21.1972 6.68759 21.0596 6.78237L20.918 6.8644L20.7969 6.92007L20.7471 6.94448H4.25001L4.2002 6.92007L4.08008 6.8644C3.9349 6.79621 3.79736 6.68326 3.69336 6.55288L3.60059 6.41714C3.57026 6.36293 3.53913 6.30414 3.52344 6.2228C3.51014 6.15365 3.51075 6.07511 3.51075 5.98647C3.51075 5.89824 3.51086 5.82146 3.52344 5.75307C3.53811 5.67377 3.56611 5.61549 3.59571 5.55972L3.68458 5.42593C3.7179 5.38326 3.75408 5.34213 3.79102 5.30581C3.86421 5.2339 3.95414 5.16319 4.04688 5.11831L4.17383 5.0685C4.22015 5.05351 4.26797 5.04154 4.30762 5.03335L4.36426 5.02651C4.37684 5.02589 4.3933 5.02506 4.41114 5.02456C4.44746 5.02355 4.49793 5.02255 4.56055 5.02163C4.68665 5.01979 4.86671 5.01836 5.0918 5.01675C5.54265 5.01352 6.17861 5.01125 6.93653 5.00893C8.45292 5.0043 10.4621 5.00088 12.4717 5.00015C14.4811 4.99941 16.4915 5.0015 18.0117 5.00503Z" />
                <path
                    :class="['burger-line', { 'burger-open opacity-0': isOpen }]"
                    class="burger-middle"
                    d="M17.9902 10.5216C18.7446 10.5231 19.3781 10.5242 19.8281 10.5265C20.0528 10.5277 20.233 10.5291 20.3594 10.5304C20.4221 10.5311 20.4724 10.5316 20.5088 10.5324C20.535 10.5329 20.5753 10.5332 20.5986 10.5363L20.7598 10.5685C20.8154 10.5827 20.8697 10.6003 20.9102 10.6183L20.9844 10.6554C21.0572 10.6974 21.1266 10.7516 21.1865 10.8068C21.2665 10.8805 21.3445 10.9716 21.3965 11.0666L21.4404 11.1554C21.4539 11.1873 21.4657 11.2224 21.4736 11.2628C21.4872 11.3328 21.4873 11.4112 21.4873 11.5001C21.4873 11.5885 21.4871 11.6652 21.4746 11.7335C21.4673 11.7731 21.4568 11.8077 21.4443 11.839L21.4023 11.9269C21.3523 12.021 21.2783 12.1117 21.2012 12.1857C21.1433 12.2411 21.0756 12.2944 21.0049 12.3371L20.9326 12.3761C20.8925 12.395 20.8398 12.4125 20.7861 12.4279L20.6318 12.463C20.6063 12.4669 20.5632 12.4683 20.5371 12.4689C20.5001 12.4698 20.449 12.471 20.3857 12.4718C20.2583 12.4734 20.0774 12.4744 19.8516 12.4757C19.399 12.4785 18.7625 12.4798 18.0049 12.4816C16.4897 12.4852 14.4861 12.4876 12.4834 12.4875C10.4804 12.4873 8.47722 12.4853 6.96485 12.4816C6.20901 12.4797 5.57524 12.4775 5.12501 12.4748C4.89997 12.4734 4.72004 12.4725 4.59376 12.4708C4.53076 12.47 4.47979 12.4688 4.44336 12.4679C4.42562 12.4675 4.40978 12.4665 4.39747 12.466L4.34766 12.4611C4.06936 12.4135 3.81695 12.2555 3.65821 12.0285L3.59571 11.9269C3.56617 11.8712 3.53801 11.8128 3.52344 11.7335C3.51092 11.6652 3.51075 11.5885 3.51075 11.5001C3.51075 11.4112 3.50986 11.3328 3.52344 11.2628C3.53927 11.1816 3.57108 11.1222 3.60157 11.0666L3.64551 10.9962C3.6933 10.9269 3.75208 10.8613 3.81251 10.8058C3.89291 10.732 3.99004 10.6619 4.08985 10.6173L4.23145 10.5695C4.28305 10.5555 4.33599 10.5442 4.37989 10.5382L4.46876 10.5333C4.50511 10.5326 4.55628 10.5321 4.61915 10.5314C4.74553 10.53 4.92562 10.5287 5.1504 10.5275C5.60023 10.5251 6.23317 10.5232 6.98731 10.5216C8.4959 10.5184 10.4923 10.5169 12.4883 10.5167C14.4844 10.5166 16.4813 10.5186 17.9902 10.5216Z" />
                <path
                    :class="['burger-line burger-line-bottom', { 'burger-open ': isOpen }]"
                    d="M20.7236 16.0558L20.7705 16.0773L20.8867 16.1291L21.043 16.2111C21.1893 16.3045 21.3082 16.4303 21.3897 16.5832L21.4316 16.673C21.5052 16.8587 21.5214 17.0766 21.4707 17.2677L21.4404 17.3605C21.3815 17.5058 21.2834 17.6376 21.1621 17.7414L21.0332 17.8351C21.0332 17.8351 21.0308 17.8374 21.0264 17.841C21.0239 17.843 21.0033 17.8609 20.9824 17.8751C20.9262 17.9135 20.8646 17.9333 20.792 17.9464C20.6653 17.9693 20.4344 17.9803 19.9629 17.9874C19.0012 18.0021 16.9526 18.0001 12.499 18.0001C8.04785 18.0001 5.99928 18.002 5.03711 17.9874C4.56478 17.9803 4.33368 17.9701 4.20704 17.9474C4.13483 17.9345 4.07381 17.9142 4.01758 17.8761C3.99705 17.8622 3.97708 17.8448 3.97364 17.8419L3.9668 17.8361C3.70861 17.6775 3.54608 17.4398 3.50879 17.1544L3.5 17.0294C3.49871 16.6036 3.75048 16.2657 4.15528 16.1115L4.32325 16.048L20.7236 16.0558Z" />
            </svg>
        </button>
    </nav>
</template>
<style scoped>
    /* See https://developer.chrome.com/docs/css-ui/edge-to-edge */
    :deep(body) {
        padding-bottom: calc(var(--navigation-height) + var(--safe-area-max-inset-bottom));
    }
    .mol-header-mobile-nav-bar {
        transition-duration: 300ms;
        /* See: https://developer.chrome.com/docs/css-ui/edge-to-edge */
        height: var(--navigation-height);
        padding-bottom: calc(4px + var(--safe-area-max-inset-bottom));
        bottom: var(--bottom-distance);
        box-shadow: 0 abs(var(--bottom-distance)) 0 0 #fff;
    }
    /* Transition timing from X to burger */
    .burger-line {
        /* property duration delay */
        transition:
            opacity 0ms 200ms,
            translate 100ms 200ms,
            rotate 200ms 0ms,
            scale 200ms 0ms;
    }
    /* Transition timing from burger to X */
    .burger-line.burger-open {
        /* property duration delay */
        transition:
            opacity 0ms 200ms,
            translate 200ms 0ms,
            rotate 300ms 200ms,
            scale 300ms 200ms;
    }
    .burger-line-top {
        transform-origin: center 6px;
    }
    .burger-line-top.burger-open {
        translate: 0px 5.5px;
        rotate: 45deg;
        scale: 1.25;
    }
    .burger-line-bottom {
        transform-origin: center 17px;
    }
    .burger-line-bottom.burger-open {
        translate: 0px -5.5px;
        rotate: -45deg;
        scale: 1.25;
    }
    .pdp-cart-bubble {
        translate: calc((100vw - 32px) / -4) 0px;
    }
</style>
